import { useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import {
  GetCountryCodesQuery,
  GetCountryCodesDocument,
} from "../../../graphql/supergraph.index";

/**
 * GraphQL cache query to fetch country codes map.
 *
 * Note: The `GetCountryCodesQuery` must be called upstream component hierarchy first. If not, there will be no cached countries.
 * This query should be used only on pages where the country query is run, search code for `VALIDATED_COUNTRY_HYDRATE` to find upstream
 * explicit queries.
 *
 * @returns {Record<string, string>} - An object mapping country alpha2code to country name.
 */
export const useCachedCountryNameMap = (): Record<string, string> => {
  const client = useApolloClient();

  const countryNameMap = useMemo(() => {
    const data = client.readQuery<GetCountryCodesQuery>({
      query: GetCountryCodesDocument,
    });

    if (!data?.countries) {
      return {};
    }

    return data.countries.edges.reduce(
      (acc, edge) => {
        if (edge.node.alpha2code && edge.node.name) {
          return {
            ...acc,
            [edge.node.alpha2code]: edge.node.name,
          };
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [client]);

  return countryNameMap;
};
